import React from 'react';
import { isEmpty } from 'lodash';

import { useStyles } from '~/assets/styles';
import AgentThinkingSection from '~/components/AiChat/AgentTimeline/AgentThinkingSection';
import { useChatController } from '~/components/AiChat/controller/ChatControllerContext';
import { useChatMixpanel } from '~/components/AiChat/hooks/useChatMixpanel';
import type { AiChatReferences } from '~/components/AiChat/types';
import { convertEventsToTimelineEntries, getThinkingTitle } from '~/components/AiChat/utils/timelineUtils';
import CliveMarkdownDisplay from '~/components/Clive/CliveMarkdownDisplay/CliveMarkdownDisplay';
import { FsIconButton } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import Text from '~/components/core/TextComponents/Text';
import { RefreshIcon } from '~/components/icons';
import { getRelativeTimeText, serverDateTimeToLocalMoment } from '~/DateTimeUtils';
import { CLIVE_CHAT_MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import cn from '~/Utils/cn';

import { MessageLoader } from '../MessageLoader/MessageLoader';

import { MessageActions } from './MessageActions';
import { NegativeFeedbackIconButton, PositiveFeedbackIconButton } from './MessageFeedbackIconButton';
import { useMessageLayout } from './MessageLayout';
import { MessageReferences } from './MessageReferences';
import type { AiMessageContentProps } from './MessageTypes';

const NO_REFERENCES: AiChatReferences = {};

/**
 * AI-specific message content component.
 * Renders agent timeline, sender header, message text, references, footer timestamp, and actions.
 */
export const AiMessageContent: React.FC<AiMessageContentProps> = ({
  className,
  text,
  sender,
  isLoading,
  exchangeId,
  onUserFeedback,
  onRegenerate,
  withFeedback,
  currentUserFeedback,
  referencesIds,
  showDisclaimer = false,
  createdAt,
  agentEvents = [],
  isGenerating = false,
  responseTime,
  onCopy,
  isFailed = false,
  onRetry,
}) => {
  const { classes } = useStyles() as unknown as { classes: { hoverableIcon: string; hoverableNonFilledIcon: string } };
  const controller = useChatController({ required: true });
  const references = controller.claim?.references ?? NO_REFERENCES;
  const { trackChatEvent } = useChatMixpanel();
  const { showSnackbar, layoutClasses } = useMessageLayout();

  const [feedbackSubmitted, setFeedbackSubmitted] = React.useState<boolean>(
    typeof currentUserFeedback === 'number' && currentUserFeedback !== 0
  );

  const handleUserFeedback = (exchangeId: string, feedbackScore: number, feedbackText?: string) => {
    if (!onUserFeedback) return;
    setFeedbackSubmitted(true);

    if (feedbackScore > 0) {
      trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_FEEDBACK_POSITIVE, { exchange_id: exchangeId });
    } else {
      trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_FEEDBACK_NEGATIVE_SUBMITTED, {
        exchange_id: exchangeId,
        has_text: !!feedbackText,
        text_length: feedbackText?.length ?? 0,
      });
    }

    onUserFeedback(exchangeId, feedbackScore, feedbackText || '');

    showSnackbar(
      feedbackScore > 0
        ? 'Thank you for your feedback!'
        : 'Thank you for your feedback, we will try to do better next time!'
    );
  };

  const handleCopyToClipboard = () => {
    trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_RESPONSE_COPIED, { exchange_id: exchangeId });
    onCopy();
    showSnackbar('Copied to clipboard');
  };

  const hasResponse = !!text;
  const actionsClasses = cn('flex items-center gap-2 opacity-0 transition-opacity group-hover:opacity-100');

  const relativeDate = createdAt ? getRelativeTimeText(serverDateTimeToLocalMoment(createdAt).toDate()) : undefined;

  // Memoize the timeline derivation so a long agent run doesn't re-sort all events and rebuild the
  // entries on every render — only when the underlying events actually change.
  const timelineEntries = React.useMemo(() => convertEventsToTimelineEntries(agentEvents), [agentEvents]);
  const thinkingTitle = React.useMemo(
    () => getThinkingTitle(agentEvents, isGenerating, responseTime),
    [agentEvents, isGenerating, responseTime]
  );

  return (
    <>
      {/* Header with Agent Timeline */}
      <div className={cn('__HEADER__ flex w-full flex-col pt-8', className)}>
        <div className="flex flex-grow flex-row items-center">
          <div className="flex flex-grow flex-col">
            {!isEmpty(agentEvents) ? (
              <div>
                <AgentThinkingSection
                  title={thinkingTitle}
                  timelineEntries={timelineEntries}
                  defaultExpanded={isGenerating}
                  isDefaultTimelineExpanded={isGenerating}
                />
              </div>
            ) : (
              // No timeline events yet but the agent is working: show the thinking title in the same
              // spot so the header doesn't pop in when the first event lands.
              isGenerating && (
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.MEDIUM} className="text-blue-600">
                  {thinkingTitle}
                </Text>
              )
            )}
            {sender && (
              <div className={cn('flex w-full flex-row', { 'justify-end': layoutClasses.isRightAligned })}>
                <span className="text-base font-bold">{sender}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Text Content */}
      <div className="__TEXT_CONTAINER__ flex w-full">
        <div className="flex w-full flex-col">
          {isFailed ? (
            <div className="flex w-full flex-col items-start gap-8">
              <Text className="text-red-700" weight={Text.WEIGHTS.REGULAR} variant={Text.VARIANTS.SM}>
                {controller.presentation.failedText}
              </Text>
              {onRetry && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<RefreshIcon size={16} />}
                  onClick={onRetry}
                >
                  Retry
                </Button>
              )}
            </div>
          ) : (
            <>
              {isLoading && <MessageLoader />}
              {!isLoading && (
                <div className="flex w-full flex-col">
                  <CliveMarkdownDisplay content={text} className="text-base" references={references} />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* References */}
      {controller.claim && (
        <div className="pt-12">
          <MessageReferences referencesIds={referencesIds} references={references} />
        </div>
      )}

      {/* Actions */}
      <div className="__MANAGEMENT_ACTIONS__ flex w-full justify-end gap-2">
        {showDisclaimer && <span className="mr-auto text-sm text-slate-800">{controller.presentation.disclaimer}</span>}
        {hasResponse && (
          <div className={actionsClasses}>
            {relativeDate && (
              <Text className="text-slate-700" weight={Text.WEIGHTS.REGULAR} variant={Text.VARIANTS.XS}>
                {relativeDate}
              </Text>
            )}
            <MessageActions type="default" onCopy={handleCopyToClipboard} />
            {showDisclaimer && onRegenerate && (
              <FsIconButton
                className={cn(classes.hoverableNonFilledIcon, 'ml-[-2px]')}
                tooltipText="Regenerate Response"
                onClick={() => {
                  trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_RESPONSE_REGENERATED, { exchange_id: exchangeId });
                  onRegenerate();
                }}
              >
                <RefreshIcon size={20} />
              </FsIconButton>
            )}
            {onUserFeedback && exchangeId && withFeedback && (
              <>
                <NegativeFeedbackIconButton
                  onClick={handleUserFeedback}
                  exchangeId={exchangeId}
                  currentScore={currentUserFeedback}
                  disabled={feedbackSubmitted}
                />
                <PositiveFeedbackIconButton
                  onClick={handleUserFeedback}
                  exchangeId={exchangeId}
                  currentScore={currentUserFeedback}
                  disabled={feedbackSubmitted}
                />
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
};
